import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { AiFillHome } from "react-icons/ai";
import Button from "@components/Button";
import debounce from "lodash/debounce";

function Breadcrumbs({ showText, heading, textContent, isLoading = false }) {
	const location = useLocation();
	const isHomePage = location.pathname === "/"; // Should be false usually if used correctly, but good to have safety
	const isArticle = /^\/articles\/.*/.test(location.pathname);
	const isServiceDetail =
		location.pathname.startsWith("/services/") &&
		location.pathname !== "/services";

	const HeadingTag = isArticle || isServiceDetail ? motion.h1 : motion.h4;

	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
	const [imageLoaded, setImageLoaded] = useState(false);
	const [showContent, setShowContent] = useState(false);

	useEffect(() => {
		const handleResize = debounce(() => {
			setIsMobile(window.innerWidth < 768);
		}, 100);
		window.addEventListener("resize", handleResize);

		// Show content animation
		setShowContent(true);

		return () => {
			window.removeEventListener("resize", handleResize);
			handleResize.cancel();
		};
	}, []);

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				duration: 0.6,
				staggerChildren: 0.4,
				delayChildren: 0.2,
			},
		},
	};

	const itemVariants = {
		hidden: { opacity: 0, y: 30, scale: 0.98 },
		visible: {
			opacity: 1,
			y: 0,
			scale: 1,
			transition: {
				duration: 0.6,
				ease: "easeOut",
			},
		},
	};

	const formatHeading = (text) => {
		if (!text) return "";
		return text.split(" ").map((word, index) => (
			<span key={index} className="inline-block mr-2">
				{word}
			</span>
		));
	};

	const Skeleton = ({ className }) => (
		<div className={`animate-pulse bg-gray-300 rounded ${className}`}></div>
	);

	Skeleton.propTypes = {
		className: PropTypes.string,
	};

	// If it's homepage, we probably shouldn't render this component,
	// or return null, but MainLayout will likely control this.
	if (isHomePage) return null;

	return (
		<div className="flex py-0 px-0 lg:px-12 relative">
			{isMobile ? (
				<motion.div
					initial="hidden"
					animate={showContent ? "visible" : "hidden"}
					variants={containerVariants}
					className="flex flex-col items-center justify-center space-y-6 font-bespokeSane w-full relative z-10"
				>
					<div className="px-4">
						<div className="flex rounded-xl overflow-hidden">
							{isLoading ? (
								<Skeleton className="w-full h-48" />
							) : (
								<div className="relative w-full">
									{!imageLoaded && (
										<Skeleton className="absolute inset-0 w-full h-60 z-10" />
									)}
									{/* Note: The original code only showed image on HomePage for mobile in one branch, 
                                        but in the 'else' (desktop) branch it showed banner-other.webp.
                                        Checking the 'restored' logic:
                                        In mobile view, it had {isHomePage && <img ...>} so NO image for non-homepage mobile?
                                        Let's re-read the component I just wrote in step 132.
                                        Line 396: {isHomePage && (<img ... />)} inside mobile view.
                                        So for mobile non-homepage, it seems there was NO image in the restored code.
                                        I will stick to that logic for now to be safe.
                                    */}
								</div>
							)}
						</div>
					</div>

					<div className="flex flex-wrap gap-2 px-4 items-center w-full">
						{isLoading ? (
							<div className="flex gap-2 items-center">
								<Skeleton className="w-4 h-4" />
								<Skeleton className="w-12 h-4" />
							</div>
						) : (
							<Link
								to="/"
								className="text-lg md:text-base flex gap-2 font-semibold font-bespokeSane items-center justify-center"
							>
								<AiFillHome className="text-steerOrange text-lg md:text-2xl" />
							</Link>
						)}

						{isServiceDetail && (
							<p className="text-lg md:text-base font-light font-bespokeSane text-steerOrange">
								/
							</p>
						)}

						{isServiceDetail && (
							<Link
								to="/services"
								className="text-lg md:text-base font-light font-bespokeSane underline underline-offset-4"
							>
								Services
							</Link>
						)}

						<p className="text-lg md:text-base font-light font-bespokeSane text-steerOrange">
							/
						</p>

						{isArticle && (
							<Link
								to="/articles"
								className="text-lg md:text-base font-light font-bespokeSane underline underline-offset-4"
							>
								Articles
							</Link>
						)}

						{isArticle && (
							<p className="text-lg md:text-base font-light font-bespokeSane text-steerOrange">
								/
							</p>
						)}

						{isLoading ? (
							<Skeleton className="w-48 h-6 mx-auto" />
						) : (
							heading && (
								<HeadingTag
									className="font-semibold font-bespokeSane flex-wrap text-center text-steerOrange lg:text-2xl text-sm uppercase"
									variants={itemVariants}
									transition={{ duration: 0.5 }}
								>
									{heading}
								</HeadingTag>
							)
						)}
					</div>

					{isLoading ? (
						<div className="px-4 space-y-2">
							<Skeleton className="w-full h-3" />
							<Skeleton className="w-3/4 h-3" />
							<Skeleton className="w-5/6 h-3" />
						</div>
					) : (
						<motion.p
							className=" text-steerLightBlack px-4 font-light text-sm"
							variants={itemVariants}
							transition={{ duration: 0.5 }}
						>
							{textContent}
						</motion.p>
					)}
				</motion.div>
			) : (
				<div className="grid grid-cols-1 lg:grid-cols-2 w-full gap-20 items-center relative z-10">
					{showText && (
						<motion.div
							className="flex flex-col justify-center pt-12"
							initial="hidden"
							animate={showContent ? "visible" : "hidden"}
							variants={containerVariants}
						>
							<div className="flex flex-col space-y-2">
								<div className="flex flex-wrap gap-4 items-center w-full">
									{isLoading ? (
										<div className="flex gap-4 items-center">
											<Skeleton className="w-4 h-4" />
											<Skeleton className="w-12 h-4" />
											<Skeleton className="w-1 h-4" />
											<Skeleton className="w-20 h-4" />
										</div>
									) : (
										<Link
											to="/"
											className="text-base flex gap-2 font-semibold font-bespokeSane items-center justify-center"
										>
											<AiFillHome className="text-steerOrange text-2xl" />
										</Link>
									)}

									{isServiceDetail && (
										<p className="text-base font-light font-bespokeSane text-steerOrange">
											/
										</p>
									)}

									{isServiceDetail && (
										<Link
											to="/services"
											className="text-base font-light font-bespokeSane underline underline-offset-4 decoration-steerOrange"
										>
											Services
										</Link>
									)}

									<p className="text-base font-light font-bespokeSane text-steerOrange">
										/
									</p>

									{isArticle && (
										<Link
											to="/articles"
											className="text-base font-light font-bespokeSane underline underline-offset-4"
										>
											Articles
										</Link>
									)}

									{isArticle && (
										<p className="text-base font-light font-bespokeSane text-steerOrange">
											/
										</p>
									)}

									{isLoading ? (
										<Skeleton className="w-64 h-6" />
									) : (
										heading && (
											<HeadingTag
												className="font-semibold font-bespokeSane text-lg flex flex-nowrap text-steerOrange xl:lg:text-2xl uppercase"
												variants={itemVariants}
												transition={{ duration: 0.5 }}
											>
												{formatHeading(heading)}
											</HeadingTag>
										)
									)}
								</div>
							</div>

							{isLoading ? (
								<div className="w-4/5 space-y-2">
									<Skeleton className="w-full h-4" />
									<Skeleton className="w-5/6 h-4" />
									<Skeleton className="w-3/4 h-4" />
								</div>
							) : (
								<motion.p
									className="w-4/5 font-light font-bespokeSane text-sm text-steerLightBlack lg:xl:text-base dark:text-white"
									variants={itemVariants}
									transition={{ duration: 0.5 }}
								>
									{textContent}
								</motion.p>
							)}
						</motion.div>
					)}

					{/* Desktop Image Section for Non-Homepage */}
					{/* In original logic: src={isHomePage ? "/images/719911.jpg" : "/images/banner-other.webp"} */}
					{isHomePage && (
						<motion.div
							className="flex rounded-xl overflow-hidden"
							initial="hidden"
							animate={showContent ? "visible" : "hidden"}
							variants={itemVariants}
						>
							{isLoading ? (
								<Skeleton className="w-full aspect-[4/3]" />
							) : (
								<div className="relative w-full">
									{!imageLoaded && (
										<Skeleton className="absolute inset-0 w-full h-full z-10" />
									)}
									<img
										src="/images/banner-other.webp"
										alt="Banner"
										className={`w-full h-auto object-cover transition-opacity duration-300 ${
											imageLoaded ? "opacity-100" : "opacity-0"
										}`}
										onLoad={() => setImageLoaded(true)}
									/>
								</div>
							)}
						</motion.div>
					)}
				</div>
			)}
		</div>
	);
}

Breadcrumbs.propTypes = {
	showText: PropTypes.bool,
	heading: PropTypes.string,
	textContent: PropTypes.string,
	isLoading: PropTypes.bool,
};

export default Breadcrumbs;
